import React, { useEffect, useState } from "react";
import { submitVote, getuserVoteHistory, getVoteDetailsById } from "../../api/Vote";
import { getUser } from "../../api/auth";
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import FaceRecognition from '../FaceRecognition/FaceRecognition';
import CloseIcon from '@mui/icons-material/Close';
import Loader from '../Loader';
import "./VoteHistory.css";
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { getUserId, getToken } from "../../api/index";
import { getHumanizedTime } from '../getHumanizedTime';

function VoteHistory() {
  const [allVotes, setAllVotes] = useState([]);
  const [openVote, setOpenVote] = useState({});
  const [openedVoteDetails, setOpenedVoteDetails] = useState('');
  const [faceRecognitionDialogOpen, setFaceRecognitionDialogOpen] = useState(false);
  const [pendingVote, setPendingVote] = useState(null);
  const [isSignIn, setIsSignIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [unmutedVideoId, setUnmutedVideoId] = useState(null);
  const [isUserVerified, setIsUserVerified] = useState('');
  const [playingVideos, setPlayingVideos] = useState({});

  useEffect(() => {
    handleVoteHistory();
  }, []);

  useEffect(() => {
    const token = getToken();
    const userId = getUserId();
    const getUserDetails = async () => {
      const response = await getUser(userId, token);
      console.log(" response from getUser api:", response.data);
      if (response.data?.governmentId === null) {
        setIsUserVerified('notUploaded');
      } else if (response.data?.governmentId?.isVerified === true) {
        setIsUserVerified('verified');
      } else if (response.data?.governmentId?.verificationStatus === 'rejected') {
        setIsUserVerified('rejected');
      }
      else {
        setIsUserVerified('pending');
      }
    }
    getUserDetails();
  }, []);

  const handleVoteHistory = async () => {
    setIsLoading(true);
    const token = getToken();
    const userId = getUserId();
    try {
      const response = await getuserVoteHistory(userId, token);
      console.log(" response from getuserVoteHistory api:", response);
      setAllVotes(response.data)
    } catch (error) {
      console.log("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitVote = async (voteId, optionId) => {
    // If the opened vote requires face verification, open the face recognition dialog
    if (openedVoteDetails && openedVoteDetails.isFaceRequired) {
      setPendingVote({ voteId, optionId });
      setFaceRecognitionDialogOpen(true);
      return;
    }

    // Otherwise submit the vote immediately (no face check required)
    setPendingVote({ voteId, optionId });
    const token = getToken();
    const userId = getUserId();
    try {
      const response = await submitVote({ voteId, userId, optionId }, token);
      console.log(" response from submitVote api:", response);
      const credentials = {
        voteId: voteId,
        userId: userId
      };
      const updatedVoteDetails = await getVoteDetailsById(credentials, token);
      setOpenedVoteDetails(updatedVoteDetails);

      // Update the allVotes array with the new selectedOptionId
      setAllVotes(prevVotes =>
        prevVotes.map(vote => {
          if (vote.voteId === voteId) {
            return {
              ...vote,
              selectedOptionId: optionId,
              selectedOptionText: updatedVoteDetails.options.find(opt => opt.id === optionId)?.value || vote.selectedOptionText
            };
          }
          return vote;
        })
      );
    } catch (error) {
      console.log("error", error);
    } finally {
      setPendingVote(null);
    }
  };

  useEffect(() => {
    if (isSignIn && pendingVote) {
      const submit = async () => {
        const token = getToken();
        const userId = getUserId();
        try {
          const response = await submitVote({ voteId: pendingVote.voteId, userId, optionId: pendingVote.optionId }, token);
          console.log(" response from submitVote api:", response);
          const credentials = {
            voteId: pendingVote.voteId,
            userId: userId
          }
          const updatedVoteDetails = await getVoteDetailsById(credentials, token);
          setOpenedVoteDetails(updatedVoteDetails);

          // Update the allVotes array with the new selectedOptionId
          setAllVotes(prevVotes =>
            prevVotes.map(vote => {
              if (vote.voteId === pendingVote.voteId) {
                return {
                  ...vote,
                  selectedOptionId: pendingVote.optionId,
                  selectedOptionText: updatedVoteDetails.options.find(opt => opt.id === pendingVote.optionId)?.value || vote.selectedOptionText
                };
              }
              return vote;
            })
          );
        } catch (error) {
          console.log("error", error);
        }
        setIsSignIn(false);
        setFaceRecognitionDialogOpen(false);
        setPendingVote(null);
      };
      submit();
    }
  }, [isSignIn, pendingVote]);


  const handleEditBtnClick = async (voteId) => {
    const newOpenState = !openVote[voteId];
    setOpenVote({
      [voteId]: newOpenState
    });

    if (!newOpenState) {
      setOpenVote({});
      setOpenedVoteDetails(''); // Clear vote details when closing
      return;
    }

    // Clear previous vote details immediately when opening a new vote
    setOpenedVoteDetails('');

    const token = getToken();
    const userId = getUserId();
    if (voteId) {
      const credentials = {
        voteId: voteId,
        userId: userId
      }
      try {
        const response = await getVoteDetailsById(credentials, token);
        console.log(" response from getVoteDetailsById api:", response);
        setOpenedVoteDetails(response);
      } catch (error) {
        console.log("error", error);
      }
    }
  };

  const handleVolumeToggle = (voteId, event) => {
    event.stopPropagation();
    setUnmutedVideoId(unmutedVideoId === voteId ? null : voteId);
  };

  const handleVideoPlayPause = (voteId, event) => {
    event.stopPropagation();
    const videoElement = event.target.closest('.voteMediaContainer').querySelector('video');

    if (videoElement) {
      if (playingVideos[voteId]) {
        videoElement.pause();
        setPlayingVideos(prev => ({ ...prev, [voteId]: false }));
      } else {
        videoElement.play();
        setPlayingVideos(prev => ({ ...prev, [voteId]: true }));
      }
    }
  };

  const handleFaceRecognitionSuccess = () => {
    setIsSignIn(true);
  };

  // Helper function to get user initials
  const getUserInitials = (userName) => {
    if (!userName) return '';
    const words = userName.trim().split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    const firstInitial = words[0] ? words[0].charAt(0).toUpperCase() : '';
    const lastInitial = words[words.length - 1] ? words[words.length - 1].charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial;
  };

  // Helper function to generate background color based on initials
  const getBackgroundColor = (initials) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
      '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
      '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA'
    ];

    let hash = 0;
    for (let i = 0; i < initials.length; i++) {
      hash = initials.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);
    return colors[hash % colors.length];
  };


  return (
    <div className="Feeds">
      <div className="feedsContainer">
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '40vh', width: '100%', gap: '20px' }}>
            <Loader containerSize="3rem" />
            <p style={{
              color: '#ffffff',
              fontWeight: '600',
              fontSize: '1rem',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              textAlign: 'center',
              maxWidth: '90%',
              lineHeight: '1.4',
              fontFamily: '"Inter", sans-serif',
              opacity: 0.8
            }}>
              Hang tight, we’re getting things ready for you...
            </p>
          </div>
        ) : allVotes === undefined || allVotes.length === 0 ? (
          <div className="feedCardContainer" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '600px',
            textAlign: 'center',
            padding: '40px 20px'
          }}>
            <div className="noVotesMessage">
              <h3 className="noVotesMessageText">No Vote History</h3>
              <p className="noVotesMessageText2">
                You haven't participated in any votes yet. Start exploring and vote on topics that interest you!
              </p>
            </div>
          </div>
        ) : (
          <div className="feedCardContainer">
            {allVotes.map((item, index) => (
              <div key={index} className="feedCard">

                <div className="feedBody">
                  {openVote[item.voteId] && (
                    <div className="feedCardHeader">
                      <div className="profileDetailsContainer" style={{ marginBottom: "10px" }}>
                        {openedVoteDetails.createdByUserPhoto ? (
                          <img
                            src={openedVoteDetails.createdByUserPhoto}
                            alt="no-img-found"
                            className="profilePic"
                          />
                        ) : (
                          <div
                            className="profilePic"
                            style={{
                              backgroundColor: getBackgroundColor(getUserInitials(openedVoteDetails.displayName)),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '16px',
                              fontFamily: 'Arial, sans-serif'
                            }}
                          >
                            {getUserInitials(openedVoteDetails.displayName ? openedVoteDetails.displayName : openedVoteDetails.createdByUserName)}
                          </div>
                        )}
                        <div className="profileDetails">
                          <span className="profileNameText">{openedVoteDetails.displayName ? openedVoteDetails.displayName : openedVoteDetails.createdByUserName}</span>
                        </div>
                      </div>
                      <div className="timeContainer">
                        {openedVoteDetails.isPaused ? <div className="pausedContainer"><span className="pausedtext">Paused</span></div> : <div className="timeContainer"><span className="timeText">{getHumanizedTime(openedVoteDetails.createdOn)}</span></div>}
                      </div>
                    </div>
                  )}

                  <div className="voteTitleContainer">
                    <div className="voteTitleTextContainer">
                      <span className="voteTitleText">
                        {item.title || item.description}
                      </span>
                      {!openVote[item.voteId] && (
                        <span className="selectedOptionText">
                          <b>Your Answer:</b> {item.selectedOptionText}
                        </span>
                      )}
                    </div>

                    <div className="editVoteContainer" onClick={() => handleEditBtnClick(item.voteId)}>
                      <EditRoundedIcon sx={{ color: "#5AAF9A", fontSize: { xs: 15, sm: 18, md: 20, lg: 25, xl: 30 } }} />
                    </div>
                  </div>

                  {openVote[item.voteId] && openedVoteDetails?.imageUrls?.length > 0 && <div className="voteMediaContainer">
                    <img
                      src={openedVoteDetails?.imageUrls?.[0]}
                      alt="no-img-found"
                      className="voteMedia"
                    />
                  </div>}
                  {openVote[item.voteId] && openedVoteDetails?.videoUrl && <div className="voteMediaContainer" style={{ position: 'relative' }}>
                    <video
                      src={openedVoteDetails?.videoUrl}
                      alt="no-video-found"
                      className="voteMedia"
                      muted={unmutedVideoId !== item.voteId}
                      loop
                      onPlay={() => setPlayingVideos(prev => ({ ...prev, [item.voteId]: true }))}
                      onPause={() => setPlayingVideos(prev => ({ ...prev, [item.voteId]: false }))}
                      onClick={(e) => handleVideoPlayPause(item.voteId, e)}
                      style={{
                        cursor: "pointer",
                      }}
                    />

                    {/* Play/Pause Button Overlay */}
                    {!playingVideos[item.voteId] && (
                      <div
                        className="playPauseOverlay"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          // background: 'rgba(0, 0, 0, 0.6)',
                          // borderRadius: '50%',
                          // padding: '15px',
                          // display: 'flex',
                          // alignItems: 'center',
                          // justifyContent: 'center',
                          cursor: 'pointer',
                          zIndex: 10
                        }}
                        onClick={(e) => handleVideoPlayPause(item.voteId, e)}
                      >
                        <PlayArrowRoundedIcon
                          sx={{
                            color: "#5AAF9A",
                            fontSize: "70px",
                            cursor: "pointer",
                          }}
                        />
                      </div>
                    )}

                    <div className="volumeControlContainer" style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: '50%',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {unmutedVideoId === item.voteId ? (
                        <VolumeUpIcon
                          onClick={(e) => handleVolumeToggle(item.voteId, e)}
                          sx={{
                            color: "#fff",
                            fontSize: "20px",
                            cursor: "pointer",
                            "&:hover": { opacity: 0.8 }
                          }}
                        />
                      ) : (
                        <VolumeOffIcon
                          onClick={(e) => handleVolumeToggle(item.voteId, e)}
                          sx={{
                            color: "#fff",
                            fontSize: "20px",
                            cursor: "pointer",
                            "&:hover": { opacity: 0.8 }
                          }}
                        />
                      )}
                    </div>
                  </div>
                  }
                  {openVote[item.voteId] && openedVoteDetails?.fileUrl && openedVoteDetails.fileUrl.endsWith('.pdf') && (
                    <div className="pdfLinkContainer">
                      <a
                        href={`https://drive.google.com/viewerng/viewer?url=${encodeURIComponent(openedVoteDetails.fileUrl)}&embedded=true`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pdfLink"
                      >
                        <img
                          src="/Assets/Sidebar/pdf-icon.svg"
                          alt="PDF"
                          className="pdfIcon"
                        />
                        View PDF
                      </a>
                    </div>
                  )
                  }
                  {openVote[item.voteId] && (
                    <div className="voteDescriptionContainer">
                      <span className="voteDescriptionText">{openedVoteDetails.description}</span>
                    </div>
                  )}
                  {openVote[item.voteId] && openedVoteDetails?.voteId === item.voteId && openedVoteDetails?.options && (
                    <div className="voteBtnsContainer">
                      {openedVoteDetails.options.map((option) => {
                        const isSelected = (openedVoteDetails.selectedOptionId || item.selectedOptionId)?.toLowerCase() === option.id?.toLowerCase();
                        return (
                          <button
                            key={option.id}
                            className={`vote-button-wrapper ${openedVoteDetails.isVoted ? 'voted' : ''} ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleSubmitVote(openedVoteDetails.voteId, option.id)}
                            disabled={openedVoteDetails.isPaused}
                          >
                            {isSelected && (
                              <div
                                className="vote-progress-bar"
                                style={{ width: `${option.percentage}%` }}
                              />
                            )}
                            <span className={`vote-text ${openedVoteDetails.isVoted && !isSelected ? 'voted' : ''}`}>{option.value}</span>
                            <span className={`vote-percentage ${openedVoteDetails.isVoted && !isSelected ? 'voted' : ''}`}>{option.percentage}%</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Dialog
        open={faceRecognitionDialogOpen}
        onClose={() => setFaceRecognitionDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: '50vw',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 3,
            overflow: 'hidden',
            marginTop: '150px',
            backgroundColor: '#0e1b1b',
            position: 'relative',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', width: '100%', pr: 5, pl: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Verify your identity
          <CloseIcon
            onClick={() => setFaceRecognitionDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: 'white',
              cursor: 'pointer',
              fontSize: 28,
              zIndex: 10,
            }}
            aria-label="close"
          />
        </DialogTitle>
        <FaceRecognition
          isUserVerified={isUserVerified}
          onSuccess={handleFaceRecognitionSuccess}
          onCancel={() => setFaceRecognitionDialogOpen(false)}
        />
      </Dialog>
    </div>
  );
}

export default VoteHistory;
